import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Clickable from 'components/Common/Clickable'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Image from 'components/Common/Image'
import { INTL_DAY_MONTH_NAME } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { useExperience } from 'tripPlanner/hooks/api/offer'
import { concatStringWithDot } from 'tripPlanner/utils'

type DisplayVariant = 'default' | 'addedToTrip'
const ADDED_TO_TRIP_CLASS_NAME = 'added-to-trip'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  &.${ADDED_TO_TRIP_CLASS_NAME} {
    width: ${rem(300)};
    min-height: ${rem(68)};
  }

  > * {
    height: 100%;
  }
`

const ThumbnailWrapper = styled.div`
  width: ${rem(44)};
  height: ${rem(58)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  flex-shrink: 0;

  &.${ADDED_TO_TRIP_CLASS_NAME} {
    width: ${rem(48)};
    height: ${rem(48)};
  }
`

const CompactContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`

interface Props {
  itemId: string
  experienceId: string
  propertyName: string
  checkInDate?: string
  time?: string
  selected?: boolean
  disabled?: boolean
  displayVariant?: DisplayVariant
  onClick: (itemId: string) => void
}

export default function ChatLandingExperienceOrderTile({
  itemId,
  experienceId,
  propertyName,
  checkInDate,
  time,
  selected,
  disabled = false,
  displayVariant = 'default',
  onClick,
}: Props) {
  const intl = useIntl()
  const isAddedToTripVariant = displayVariant === 'addedToTrip'
  const displayVariantClassName = isAddedToTripVariant ? ADDED_TO_TRIP_CLASS_NAME : undefined
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const intlDateFormatter = useIntlDateFormatter()
  const { data: experience } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  const subtitle = useMemo(() => {
    const formattedDate = checkInDate ?
        intlDateFormatter(checkInDate, INTL_DAY_MONTH_NAME) :
      ''

    return concatStringWithDot(formattedDate, time ?? '')
  }, [checkInDate, intlDateFormatter, time])

  const yourBookingLabel = useMemo(() => intl.formatMessage({
    id: 'dNPOURpXZmUVjbqyPKX3',
    defaultMessage: 'Your booking',
    description: 'Category label for upcoming booking tile on the chat landing page',
  }), [intl])

  const handleClick = useCallback(() => {
    if (disabled) {
      return
    }

    onClick(itemId)
  }, [disabled, onClick, itemId])

  const thumbnailImage = experience?.images[0]

  const tileContent = (
    <Group
      direction="horizontal"
      gap={isAddedToTripVariant ? 8 : 12}
      verticalAlign="center"
      grow
    >
      {thumbnailImage && <ThumbnailWrapper className={displayVariantClassName}>
        <Image image={thumbnailImage} alt={propertyName} fit="center" objectFit="cover" />
      </ThumbnailWrapper>}
      <Group direction="vertical" gap={2} grow>
        <BodyText
          variant="medium"
          weight="normal"
          lineClamp={isAddedToTripVariant ? 1 : 2}
        >
          {propertyName}
        </BodyText>
        {subtitle && <Caption variant="medium" colour="light-foreground-alt" lineClamp={1}>
          {subtitle}
        </Caption>}
      </Group>
    </Group>
  )

  const compactContent = (
    <CompactContent>{tileContent}</CompactContent>
  )

  const containerContent = isAddedToTripVariant ? compactContent : (
    <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
      <CaptionTextBlock
        variant="small"
        weight="bold"
        format="uppercase"
        colour="light-foreground-alt"
        startIcon={<LineCalendarIcon size="XS" />}
      >
        {yourBookingLabel}
      </CaptionTextBlock>
      {tileContent}
    </Group>
  )

  return (
    <Container
      onClick={handleClick}
      disabled={disabled}
      className={displayVariantClassName}
    >
      <Sheet
        background="normal-background"
        border={selected ? 'normal-contrast' : 'light-foreground'}
        borderRadius="L"
        padding={isAddedToTripVariant ? 6 : 12}
      >
        {containerContent}
      </Sheet>
    </Container>
  )
}
